import { auth } from "./firebase";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "/api";

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

async function authHeaders(): Promise<HeadersInit> {
  const user = auth.currentUser;
  if (!user) return {};
  const token = await user.getIdToken();
  return { Authorization: `Bearer ${token}` };
}

export async function apiGet<T>(path: string): Promise<T> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE_URL}${path}`, { headers });
  if (!res.ok) throw new ApiError(res.status, await res.text());
  return res.json() as Promise<T>;
}

export async function apiPost<T>(path: string, body: unknown): Promise<T> {
  const headers = await authHeaders();
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...headers },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new ApiError(res.status, await res.text());
  return res.json() as Promise<T>;
}

// ─── Domain types (mirror backend Pydantic) ───────────────────────────────
export interface CertDomain {
  id: string;
  name: string;
  weight: number;
  description?: string;
}

export interface CertSpec {
  id: string;
  name: string;
  vendor: string;
  code: string;
  description: string;
  total_questions: number;
  duration_minutes: number;
  passing_score: number | null;
  domains: CertDomain[];
}

export interface QuestionOption {
  id: string;
  text: string;
}

export interface AttemptResult {
  question_id: string;
  cert_id: string;
  domain: string;
  is_correct: boolean;
}

export interface QuestionPublic {
  id: string;
  cert_id: string;
  domain: string;
  type: "single_choice" | "multi_choice" | "scenario";
  difficulty: 1 | 2 | 3;
  stem: string;
  options: QuestionOption[];
  tags: string[];
}

/** Full question shape (answers included) — admin authoring only. */
export interface Question extends QuestionPublic {
  correct_option_ids: string[];
  explanation: string;
  /** Per-option rationale (option_id -> why right / why the trap). Empty for legacy questions. */
  option_explanations: Record<string, string>;
  sources: string[];
  source: string;
  reviewed: boolean;
}

export interface Me {
  uid: string;
  email: string | null;
  is_admin: boolean;
}

export interface DomainStats {
  domain: string;
  attempts: number;
  correct: number;
  accuracy: number;
}

export interface CertStats {
  cert_id: string;
  attempts: number;
  correct: number;
  accuracy: number;
  domains: DomainStats[];
}

export interface DayActivity {
  date: string;
  attempts: number;
  correct: number;
}

export interface Stats {
  total_attempts: number;
  total_correct: number;
  accuracy: number;
  streak_days: number;
  due_today: number;
  per_cert: CertStats[];
  last_days: DayActivity[];
}

export interface DomainCount {
  domain: string;
  reviewed: number;
  pending: number;
}

export interface CertCounts {
  cert_id: string;
  reviewed: number;
  pending: number;
  domains: DomainCount[];
}

export interface GeneratePayload {
  cert_id: string;
  domain: string;
  n: number;
  difficulty: 1 | 2 | 3;
  answer_count: number;
}

export const api = {
  listCerts: () => apiGet<CertSpec[]>("/certs"),
  getCert: (id: string) => apiGet<CertSpec>(`/certs/${id}`),
  practiceQuestions: (certId: string, domain?: string, limit = 10) =>
    apiGet<QuestionPublic[]>(
      `/questions/practice?cert_id=${certId}${domain ? `&domain=${domain}` : ""}&limit=${limit}`,
    ),
  revealAnswer: (qid: string) =>
    apiGet<{
      correct_option_ids: string[];
      explanation: string;
      option_explanations: Record<string, string>;
      sources: string[];
    }>(`/questions/${qid}/answer`),
  recordAttempt: (payload: {
    question_id: string;
    selected_option_ids: string[];
    mode: string;
    time_taken_seconds: number;
    rating?: 1 | 2 | 3 | 4;
  }) => apiPost<AttemptResult>("/attempts", payload),
  dueCards: (certId?: string, limit = 20) =>
    apiGet<QuestionPublic[]>(
      `/review/due?${certId ? `cert_id=${certId}&` : ""}limit=${limit}`,
    ),
  rateCard: (question_id: string, rating: 1 | 2 | 3 | 4) =>
    apiPost<{ next_due: string }>("/review/rate", { question_id, rating }),
  me: () => apiGet<Me>("/me"),
  stats: () => apiGet<Stats>("/stats"),
  generateQuestions: (payload: GeneratePayload) =>
    apiPost<Question[]>("/questions/generate", payload),
  pendingQuestions: (certId?: string) =>
    apiGet<Question[]>(`/questions/pending${certId ? `?cert_id=${certId}` : ""}`),
  questionCounts: () => apiGet<CertCounts[]>("/questions/counts"),
  reviewQuestion: (qid: string, approve: boolean) =>
    apiPost<{ id: string; status: string }>(`/questions/${qid}/review`, { approve }),
};
